import { isEmpty, isNonEmpty } from '@sgme/fp';
import { guessDateFormats } from 'components/shared/DatePicker/utils';
import type { IntlKey } from 'locales';
import type { MessageFormatter } from 'locales/types';
import { dropRightWhile } from 'lodash-es';
import type { FieldMetadata } from 'models/forms/fields/FieldMetadata';
import { parsePastedBoolean } from 'utils/boolean';
import { parsePastedDate, parsePastedDatesWithErrorMessage, parsePastedDateWithErrorMessage } from 'utils/date';
import { parsePastedNumber, parsePastedNumberWithErrorMessage } from 'utils/number';

interface Row {
  [id: string]: unknown;
}

export function parsePastedValue(text: string, metadata: FieldMetadata, dateFormat: string) {
  switch (metadata.type) {
    case 'PercentField':
    case 'NumberField':
    case 'AmountField':
    case 'CustomizableTenorField':
      return parsePastedNumber(text);
    case 'DateField':
      return parsePastedDate(text, dateFormat);
    case 'ToggleField':
      return parsePastedBoolean(text);
    case 'ListField':
    case 'CurrencyListField':
    case 'TextField':
    case 'RadioButtonsField':
    case 'ClientListField':
      return text;
    default:
      return undefined;
  }
}

export function parsePastedValueWithErrorMessage(
  text: string,
  dataType: FieldMetadata['type'],
  dateFormat: string,
  formatErrorMessage: MessageFormatter,
) {
  switch (dataType) {
    case 'PercentField':
    case 'NumberField':
    case 'AmountField':
      return parsePastedNumberWithErrorMessage(text, formatErrorMessage);
    case 'DateField':
      return parsePastedDateWithErrorMessage(text, dateFormat, formatErrorMessage);
    case 'DatesListField':
      return parsePastedDatesWithErrorMessage(text, dateFormat, formatErrorMessage);
    case 'TextField':
    case 'ToggleField':
      return text;
    default:
      return undefined;
  }
}

export function getPastedData(pastedEvent: ClipboardEvent): string | undefined {
  const pastedData = pastedEvent.clipboardData?.getData('text');
  pastedEvent.preventDefault();
  return pastedData;
}

export function parseTsvRow(pastedRow: string): string[] {
  const cells = pastedRow.split(/\t|;/).map(x => x?.trim());
  // remove extra empty values at the end
  return dropRightWhile(cells, isEmpty);
}

export function getColumnCount(grid: string[][] | undefined): number {
  if (isEmpty(grid)) {
    return 0;
  }
  return grid.reduce((agg, v) => Math.max(agg, v.length), 0);
}

export function parseTsvGrid(pastedData: string | undefined): string[][] | undefined {
  if (pastedData === undefined) {
    return undefined;
  }
  const rows = pastedData
    .split(/\r\n|\r|\n/g)
    .filter(isNonEmpty)
    .map(parseTsvRow);
  // remove extra blank lines at the end
  return dropRightWhile<string[]>(rows, isEmpty);
}

export function mapPastedRow(
  currentRow: Row,
  pastedRow: string[],
  columns: FieldMetadata[],
  localDateFormat: string,
  formatMsg: MessageFormatter,
): Row {
  return pastedRow.reduce((patchedRow, pastedValue, columnIndex) => {
    const col = columns[columnIndex];
    if (col === undefined) {
      return patchedRow;
    }
    const columnLabel = formatMsg({ id: `form.${columns[columnIndex].id}` as IntlKey });
    const formatColumnError: MessageFormatter = (p, data) => formatMsg(p, { ...data, column: columnLabel });

    const parsedValue = parsePastedValueWithErrorMessage(pastedValue, col.type, localDateFormat, formatColumnError);

    return {
      ...patchedRow,
      [col.id]: parsedValue,
    };
  }, currentRow);
}

interface DetectFormatArgs {
  dates: string[];
  dateFormats: string[];
  formatMsg: MessageFormatter;
  onWarning: (msg: string) => void;
  onError: (msg: string) => void;
}

export function tryToDetectDateFormat({
  dates,
  dateFormats,
  formatMsg,
  onWarning,
  onError,
}: DetectFormatArgs): string | null {
  if (dates.length === 0 || dateFormats.length === 0) {
    return 'yyyyMMdd';
  }
  const detectedFormats = guessDateFormats(dates, dateFormats);
  const detectedFormat = detectedFormats?.[0];
  if (detectedFormat === undefined) {
    const msg = formatMsg(
      { id: 'moreMenu.pasteFromClipboard.error.canNotDetectDateFormat' },
      {
        value: dates.shift(),
        defaultFormat: dateFormats[0].toUpperCase(),
      },
    );
    onError(msg);
    return null;
  }
  if (detectedFormats.length > 1) {
    const msg = formatMsg(
      { id: 'moreMenu.pasteFromClipboard.error.ambiguousDateFormat' },
      {
        detected: detectedFormat?.toUpperCase(),
      },
    );
    onWarning(msg);
  }
  return detectedFormat;
}
